import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { integrationCategories } from '../data/mockData';
import IntegrationLogo from './IntegrationLogo';
import { useScrollReveal } from '../hooks/useScrollReveal';

const IntegrationsScroller = () => {
  const [ref, isVisible] = useScrollReveal();

  // Flatten all partners across categories, then duplicate for seamless loop.
  const partners = useMemo(() => {
    const flat = integrationCategories.flatMap((cat) => cat.partners);
    return [...flat, ...flat];
  }, []);

  return (
    <section
      ref={ref}
      id="integrations"
      className="py-20 lg:py-28 bg-gray-50 overflow-hidden"
      data-testid="integrations-scroller"
    >
      <div className="section-container">
        <div
          className={`text-center max-w-3xl mx-auto mb-12 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <p
            className="text-[10px] font-semibold uppercase tracking-[3px] text-[#00BCE4] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Works With Your Stack
          </p>
          <h2
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Integration <span className="text-[#00BCE4]">Partners</span>
          </h2>
          <p
            className="text-base text-gray-600 leading-relaxed"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            From CIP4/JDF workflows to accounting, web-to-print, machine data and shipping —
            Imprint plays nicely with the tools your business already runs on.
          </p>
        </div>
      </div>

      {/* Auto-scrolling logo track */}
      <div className="relative">
        <div className="scroll-track flex gap-4 py-4">
          {partners.map((partner, idx) => (
            <div key={`${partner.name}-${idx}`} className="flex-shrink-0">
              <IntegrationLogo partner={partner} size="md" />
            </div>
          ))}
        </div>
        {/* Edge fades */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-gray-50 to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-gray-50 to-transparent" />
      </div>

      <div className="section-container">
        <div className="text-center mt-12">
          <Link
            to="/integrations"
            data-testid="integrations-view-all-cta"
            className="btn-primary text-xs"
          >
            View All Integrations
            <ArrowRight size={16} />
          </Link>
        </div>
      </div>

      <style>{`
        .scroll-track {
          animation: integrations-scroll 60s linear infinite;
          width: max-content;
        }
        .scroll-track:hover { animation-play-state: paused; }
        @keyframes integrations-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </section>
  );
};

export default IntegrationsScroller;
